import { Formik } from "formik";
import { Row, Col, Button, Form, Image } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import AuthenticationForm from "../../lib/components/authentication/authentication-form";
import FlexBox from "../../lib/components/common/flex-box";
import AuthenticationLayout from "../../lib/components/layouts/authentication";
import Hyperlink from "lib/components/common/hyperlink";
import useApi from "lib/hooks/useApi";
import { API_ROUTES } from "lib/constants/api-constants";

import {
  EMAIL_VALIDATION_SCHEMA,
  NAME_VALIDATION_SCHEMA,
  PASSWORD_VALIDATION_SCHEMA,
  PHONE_NUMBER_VALIDATION_SCHEMA,
  SIGN_UP_VALIDATION_SCHEME,
} from "lib/constants/validation-constants";
import { getFullName } from "lib/utils/util";
import SimplePasswordField from "lib/components/common/simple-password";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { DEFAULT_SIGNUP_VALUES } from "lib/constants/default-values";
import { ASSETS } from "assets";
import SimpleTextbox from "lib/components/common/simple-textbox";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_V2_SITE_KEY || '';

const Signup = () => {
  const navigate = useNavigate();
  const { postData, loading } = useApi<GeneralAPIResponse>();

  return (
    <AuthenticationLayout>
      <FlexBox justifyContent="center" alignItems="center" className="h-100 w-100">
        <Row className="w-100 d-flex align-items-center">
          <Col
             lg={8}
            className="d-none d-lg-flex justify-content-center align-items-center"
          >
            <Image
              src={ASSETS.IMAGES.LOGIN_IMAGE}
              alt="Sign Up"
              className="img-fluid login-image"
            />
          </Col>

          <Col lg={4} md={12} className="d-flex justify-content-center">
            <AuthenticationForm
              isLoading={loading}
              className="w-100 px-3"
            >
              <FlexBox justifyContent="start" className="mb-4">
                <h2 className="text-center text-md-start">Sign Up</h2>
              </FlexBox>

              <Formik
                initialValues={{ ...DEFAULT_SIGNUP_VALUES, recaptchaToken: "" }}
                validationSchema={SIGN_UP_VALIDATION_SCHEME}
                onSubmit={async (values: SignUpRequest, { setSubmitting }) => {
                  if (!values.recaptchaToken) {
                    alert("Please verify you are not a robot.");
                    return;
                  }
                  try {
                    setSubmitting(true);
                    await postData(
                      API_ROUTES.PATIENT.signup,
                      {
                        name: getFullName(values.firstName, values.lastName),
                        email: values.email,
                        phone: values.contactNumber,
                        password: values.password,
                      },
                      () => {
                        navigate(PAGE_ROUTES.AUTHENTICATION.Login);
                      }
                    );
                  } catch (error) {
                    console.log("error", error);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col xs={12} md={6}>
                        <SimpleTextbox
                          label="First Name"
                          name="firstName"
                          onChange={handleChange}
                          value={values.firstName}
                          required
                          validationSchema={NAME_VALIDATION_SCHEMA(
                            "Please enter first name"
                          )}
                        />
                    {/* <Form.Group className="form-group">
                      <Form.Label>
                        First Name <span className="login-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                      />
                    </Form.Group> */}
                      </Col>
                      <Col xs={12} md={6}>
                        <SimpleTextbox
                          label="Last Name"
                          name="lastName"
                          onChange={handleChange}
                          value={values.lastName}
                          required
                          validationSchema={NAME_VALIDATION_SCHEMA(
                            "Please enter last name"
                          )}
                        />
                    {/* <Form.Group className="form-group">
                      <Form.Label>
                        Last Name <span className="login-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                      />
                    </Form.Group> */}
                      </Col>
                    </Row>

                    <SimpleTextbox
                      label="Email"
                      name="email"
                      onChange={handleChange}
                      value={values.email}
                      required
                      validationSchema={EMAIL_VALIDATION_SCHEMA}
                    />
                {/* <Form.Group className="form-group">
                  <Form.Label>
                    Email <span className="login-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    className="form-control"
                    onChange={handleChange}
                    required
                  />
                </Form.Group> */}

                    <SimpleTextbox
                      label="Contact No."
                      name="contactNumber"
                      onChange={handleChange}
                      value={values.contactNumber}
                      required
                      validationSchema={PHONE_NUMBER_VALIDATION_SCHEMA}
                    />

                {/* <Form.Group className="form-group">
                  <Form.Label>
                    Contact No. <span className="login-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="contactNumber"
                    value={values.contactNumber}
                    onChange={handleChange}
                    required
                    className="form-control"
                  />
                </Form.Group> */}

                    <SimplePasswordField
                      isShowVisibilityIcon
                      name="password"
                      label="Password"
                      required
                      value={values.password}
                      onChange={handleChange}
                      validationSchema={PASSWORD_VALIDATION_SCHEMA}
                    />

                    <SimplePasswordField
                      isShowVisibilityIcon
                      name="confirmPassword"
                      label="Confirm Password"
                      required
                      value={values.confirmPassword}
                      onChange={handleChange}
                      validationSchema={PASSWORD_VALIDATION_SCHEMA}
                      error={
                        touched.confirmPassword &&
                        touched.password &&
                        values.confirmPassword &&
                        values.password !== values.confirmPassword
                          ? "Passwords do not match"
                          : null
                      }
                    />

                    {/* Add reCAPTCHA */}
                    <div className="mb-3 text-center">
                      <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) =>
                          setFieldValue("recaptchaToken", token)
                        }
                      />
                    </div>

                    <Form.Group className="form-group login-btn text-center">
                      <Button
                        className="btn btn-primary px-4 w-md-auto"
                        type="submit"
                      >
                        Sign up
                      </Button>
                    </Form.Group>
                  </Form>
                )}
              </Formik>

              <p className="text-center px-2 mt-2">
                Already Have an account?{" "}
                <Hyperlink to={PAGE_ROUTES.AUTHENTICATION.Login}>
                  Sign In
                </Hyperlink>
              </p>
            </AuthenticationForm>
          </Col>
        </Row>
      </FlexBox>
    </AuthenticationLayout>
  );
};

export default Signup;
